/**
 * Gallery lightbox — GLightbox (vanilla JS) with prev/next navigation.
 *
 * Local WP often rewrites /wp-content/uploads/* to HTML (200 text/html),
 * while <img src> still points at a real image (e.g. production CDN).
 * Always open the img's loaded URL, not a broken local href.
 */
import GLightbox from 'glightbox';

let lightboxInstance = null;

function isEnglish() {
  return typeof window.english !== 'undefined' && window.english === true;
}

function localizeCaptionHtml(html) {
  if (typeof html !== 'string' || html.indexOf('|') === -1) {
    return html || '';
  }
  const parts = html.split('|');
  if (parts.length < 2) {
    return html;
  }
  return isEnglish() ? parts[parts.length - 1].trim() : parts[0].trim();
}

function applyCaptionLanguage() {
  document.querySelectorAll('.wp-caption-text > p, .gallery-caption > p').forEach((el) => {
    const html = el.innerHTML;
    if (typeof html === 'string' && html.indexOf('|') !== -1) {
      el.innerHTML = localizeCaptionHtml(html);
    }
  });
}

/**
 * Prefer the URL the browser already loaded for the thumbnail.
 */
function resolveImageUrl(anchor) {
  const img = anchor.querySelector('img');
  if (img) {
    if (img.currentSrc) {
      return img.currentSrc;
    }
    if (img.src) {
      return img.src;
    }
  }
  return anchor.getAttribute('href') || '';
}

function nestHoverCaptions() {
  document.querySelectorAll('.wc-gallery-captions-onhover .gallery-item').forEach((item) => {
    const icon = item.querySelector('.gallery-icon');
    const caption = item.querySelector(':scope > .gallery-caption, :scope > .wp-caption-text');
    if (icon && caption) {
      icon.appendChild(caption);
    }
  });
}

function prepareGalleries() {
  nestHoverCaptions();

  const galleries = document.querySelectorAll('.gallery-link-file, .wc-gallery .gallery, .gallery');
  let galleryIndex = 0;

  galleries.forEach((gallery) => {
    const links = gallery.querySelectorAll('.gallery-item:not(.clone) .gallery-icon a');
    if (!links.length) {
      return;
    }

    const galleryId = `gt-gallery-${galleryIndex++}`;
    links.forEach((anchor) => {
      const imageUrl = resolveImageUrl(anchor);
      if (!imageUrl) {
        return;
      }

      anchor.classList.add('glightbox');
      anchor.setAttribute('data-gallery', galleryId);
      anchor.setAttribute('data-type', 'image');
      // GLightbox reads href / data-href for the slide source
      anchor.setAttribute('href', imageUrl);
      anchor.setAttribute('data-href', imageUrl);

      const item = anchor.closest('.gallery-item');
      const captionEl = item
        ? item.querySelector('.gallery-caption, .wp-caption-text')
        : null;
      if (captionEl) {
        const desc = localizeCaptionHtml(captionEl.innerHTML);
        if (desc) {
          anchor.setAttribute('data-description', desc);
        }
      }
    });
  });
}

function initGalleryLightbox() {
  applyCaptionLanguage();
  prepareGalleries();

  if (!document.querySelector('a.glightbox')) {
    return;
  }

  if (lightboxInstance && typeof lightboxInstance.destroy === 'function') {
    lightboxInstance.destroy();
    lightboxInstance = null;
  }

  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  lightboxInstance = GLightbox({
    selector: 'a.glightbox',
    touchNavigation: true,
    loop: true,
    openEffect: reduceMotion ? 'none' : 'fade',
    closeEffect: reduceMotion ? 'none' : 'fade',
    slideEffect: reduceMotion ? 'none' : 'slide',
    moreLength: 0,
    // GLightbox falls back to img alt / anchor title when data-title is empty;
    // strip the title node so only the caption description shows.
    afterSlideLoad: ({ slide }) => {
      const titleEl = slide.querySelector('.gslide-title');
      if (titleEl) {
        titleEl.remove();
      }
    },
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGalleryLightbox);
} else {
  initGalleryLightbox();
}

document.body.addEventListener('post-load', initGalleryLightbox);
